import React from 'react';
import styled from 'styled-components';
import { FormNextLink } from 'grommet-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '../../../pulsechain_components/Text';
import { Link } from '../../../pulsechain_components/Link';
import {
  DepositStatus,
  TransactionStatus,
  ValidatorDepositStatus,
} from '../../../store/actions/depositFileActions';
import { BEACONCHAIN_URL, ETHERSCAN_URL } from '../../../utils/envVars';
import ReactTooltip from 'react-tooltip';

const Container = styled.div<{ disabled?: boolean }>`
  display: flex;
  border: 1px solid ${p => p.theme.gray.medium};
  padding: 4px;
  justify-content: center;
  border-radius: 4px;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  :hover {
    background: ${p => (p.disabled ? 'transparent' : p.theme.gray.medium)};
    box-shadow: ${p =>
      p.disabled ? 'none' : '0px 8px 17px rgba(0, 0, 0, 0.15)'};
    transition: transform 0.1s;
    transform: ${p => (p.disabled ? 'none' : 'scale(1.02)')};
  }
`;

const ButtonText = styled((Text as any) as any)`
  line-height: inherit;
  font-size: 16px;
  text-align: center;
`;

const ButtonLink = styled(Link as any)`
  line-height: inherit;
  font-size: 16px;
  text-align: center;
`;

interface Props {
  transactionStatus: TransactionStatus;
  depositStatus: DepositStatus;
  validatorDepositStatus?: ValidatorDepositStatus;
  txHash?: string;
  onClick: (e: any) => void;
  pubkey?: string;
}

export const ActionButton = ({
  transactionStatus,
  depositStatus,
  validatorDepositStatus,
  txHash,
  onClick,
  pubkey,
}: Props) => {
  const { formatMessage } = useIntl();

  // Check if validator is already deposited on chain (prevents double deposits)
  const isAlreadyOnChain =
    validatorDepositStatus === ValidatorDepositStatus.ACTIVE ||
    validatorDepositStatus === ValidatorDepositStatus.PENDING_INIT ||
    validatorDepositStatus === ValidatorDepositStatus.PENDING_QUEUED ||
    validatorDepositStatus === ValidatorDepositStatus.DEPOSITED_WAITING ||
    validatorDepositStatus === ValidatorDepositStatus.EXITING ||
    validatorDepositStatus === ValidatorDepositStatus.EXITED ||
    validatorDepositStatus === ValidatorDepositStatus.SLASHED ||
    validatorDepositStatus === ValidatorDepositStatus.WITHDRAWN;

  if (depositStatus === DepositStatus.ALREADY_DEPOSITED || isAlreadyOnChain) {
    return (
      <ButtonLink
        to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
        className="mr5"
        data-tip
        shouldOpenNewTab
      >
        PulseChain Beacon
      </ButtonLink>
    );
  }
  if (transactionStatus === TransactionStatus.READY) {
    return (
      <Container onClick={onClick}>
        <ButtonText>
          <FormattedMessage defaultMessage="Submit Deposit" />
        </ButtonText>
      </Container>
    );
  }
  if (transactionStatus === TransactionStatus.PENDING) {
    return <div className="flex" />;
  }
  if (transactionStatus === TransactionStatus.STARTED) {
    return (
      <div className="flex">
        <ButtonLink shouldOpenNewTab to={`${ETHERSCAN_URL}/${txHash}`}>
          PulseChain Scan
        </ButtonLink>
      </div>
    );
  }
  if (transactionStatus === TransactionStatus.SUCCEEDED) {
    return (
      <div className="flex">
        <span
          data-for="beaconchain-warning"
          data-tip={formatMessage({
            defaultMessage:
              'Note: the Beacon Chain may take several minutes to verify your deposit',
          })}
        >
          <ButtonLink
            to={`${BEACONCHAIN_URL}/validator/0x${pubkey}`}
            className="mr5"
            shouldOpenNewTab
            data-tip
          >
            PulseChain Beacon
          </ButtonLink>
        </span>
        <ReactTooltip id="beaconchain-warning" place="top" effect="solid" />
      </div>
    );
  }

  if (transactionStatus === TransactionStatus.REJECTED) {
    return (
      <Container onClick={onClick}>
        <ButtonText>
          <FormattedMessage defaultMessage="Try again" />
        </ButtonText>
        <FormNextLink />
      </Container>
    );
  }

  return (
    <Text>
      <FormattedMessage defaultMessage="An error occurred" />
    </Text>
  );
};
